/**
 * The investigations that are evidence for a hypothesis (PRD-0075 R-E3/R-E4): those saved from
 * its research and those added with *Pridať dôkaz*, each opening its page; and the assessment
 * notes written on them, newest first, which is how an investigation's judgment shows up here.
 */
import { FlaskConical, Plus, RefreshCw, Scale, Star, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import type { AssessmentNote, LinkedInvestigation } from '@/types/analytic-hypothesis'
import { formatDateTimeLocalized } from '@/utils/date'
import type { EvidenceOption } from './EvidencePicker'
import { HypothesisSection } from './HypothesisSection'

const COPY = 'analyticHypotheses.evidence'

export type LinkedInvestigationsView =
  | { status: 'loading' }
  | { status: 'failed'; onRetry: () => void }
  | { status: 'ready'; investigations: readonly LinkedInvestigation[] }

/** Linking and unlinking, for a collaborator; adding needs an open hypothesis. */
export interface EvidenceLinking {
  /** Investigations of the case not yet linked here; null while read, null ``onLink`` when closed. */
  candidates: readonly EvidenceOption[] | null
  onLink: ((investigationId: string) => void) | null
  onUnlink: (investigation: LinkedInvestigation) => void
  pending: boolean
}

export interface HypothesisEvidenceSectionProps {
  view: LinkedInvestigationsView
  investigationHref: (investigationId: string) => string
  /** Null for a reader. */
  linking: EvidenceLinking | null
}

function AddEvidence({ linking }: { linking: EvidenceLinking }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const { onLink } = linking
  if (!onLink) return null
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" disabled={linking.pending}>
          <Plus aria-hidden />
          {t(`${COPY}.add`)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[calc(100vw-2rem)] p-0 md:w-80" align="end">
        <Command>
          <CommandInput placeholder={t(`${COPY}.search`)} className="h-9" />
          <CommandList className="max-h-64">
            <CommandEmpty>
              {linking.candidates === null
                ? t('analyticHypotheses.form.loadingOptions')
                : t(`${COPY}.noCandidates`)}
            </CommandEmpty>
            <CommandGroup>
              {(linking.candidates ?? []).map(item => (
                <CommandItem
                  key={item.id}
                  value={item.id}
                  keywords={[item.label, item.hint ?? '']}
                  onSelect={() => {
                    setOpen(false)
                    onLink(item.id)
                  }}
                >
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate">{item.label}</span>
                    {item.hint && (
                      <span className="truncate text-2xs text-muted-foreground">{item.hint}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

function InvestigationRow({
  investigation,
  href,
  linking,
}: {
  investigation: LinkedInvestigation
  href: string
  linking: EvidenceLinking | null
}) {
  const { t } = useTranslation()
  return (
    <li className="flex items-center gap-2 rounded-md border border-border/60 px-3 py-2 text-sm">
      <Link to={href} className="min-w-0 flex-1 truncate text-foreground hover:underline">
        {investigation.title}
      </Link>
      {investigation.source_run_id && (
        <Badge variant="muted">
          <FlaskConical aria-hidden />
          {t(`${COPY}.fromResearch`)}
        </Badge>
      )}
      {investigation.included_in_report && (
        <Badge variant="default" title={t(`${COPY}.inReport`)}>
          <Star aria-hidden />
          <span className="max-md:sr-only">{t(`${COPY}.inReport`)}</span>
        </Badge>
      )}
      {linking && (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          disabled={linking.pending}
          aria-label={t(`${COPY}.remove`, { title: investigation.title })}
          onClick={() => linking.onUnlink(investigation)}
        >
          <X aria-hidden />
        </Button>
      )}
    </li>
  )
}

/** Every assessment note of the linked investigations, newest first, with where it was written. */
function AssessmentNotes({
  investigations,
  investigationHref,
}: {
  investigations: readonly LinkedInvestigation[]
  investigationHref: (investigationId: string) => string
}) {
  const { t, i18n } = useTranslation()
  const notes = investigations
    .flatMap(investigation => investigation.assessment_notes.map(note => ({ note, investigation })))
    .sort((a, b) => Date.parse(b.note.created_at) - Date.parse(a.note.created_at))
  const row = ({
    note,
    investigation,
  }: {
    note: AssessmentNote
    investigation: LinkedInvestigation
  }) => (
    <li key={`${investigation.id}:${note.id}`} className="flex gap-2 border-l-2 border-border pl-3">
      <Scale aria-hidden className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
      <div className="flex min-w-0 flex-col gap-1">
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">{note.text}</p>
        <p className="text-2xs text-muted-foreground">
          {note.author_name} · {formatDateTimeLocalized(note.created_at, i18n.language)} ·{' '}
          <Link to={investigationHref(investigation.id)} className="hover:underline">
            {investigation.title}
          </Link>
        </p>
      </div>
    </li>
  )
  return (
    <HypothesisSection title={t(`${COPY}.notesTitle`)} hint={t(`${COPY}.notesHint`)}>
      {notes.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t(`${COPY}.notesEmpty`)}</p>
      ) : (
        <ul className="flex flex-col gap-3">{notes.map(row)}</ul>
      )}
    </HypothesisSection>
  )
}

export function HypothesisEvidenceSection({
  view,
  investigationHref,
  linking,
}: HypothesisEvidenceSectionProps) {
  const { t } = useTranslation()
  const action = linking && <AddEvidence linking={linking} />
  return (
    <>
      <HypothesisSection title={t(`${COPY}.title`)} hint={t(`${COPY}.hint`)} action={action}>
        {view.status === 'loading' && <Skeleton aria-hidden className="h-10 w-full" />}
        {view.status === 'failed' && (
          <div role="alert" className="flex flex-wrap items-center gap-2 text-sm">
            <p className="text-destructive">{t(`${COPY}.loadFailed`)}</p>
            <Button type="button" variant="outline" size="xs" onClick={view.onRetry}>
              <RefreshCw aria-hidden />
              {t('analyticHypotheses.detail.retry')}
            </Button>
          </div>
        )}
        {view.status === 'ready' &&
          (view.investigations.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t(`${COPY}.empty`)}</p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {view.investigations.map(investigation => (
                <InvestigationRow
                  key={investigation.id}
                  investigation={investigation}
                  href={investigationHref(investigation.id)}
                  linking={linking}
                />
              ))}
            </ul>
          ))}
      </HypothesisSection>
      {view.status === 'ready' && (
        <AssessmentNotes
          investigations={view.investigations}
          investigationHref={investigationHref}
        />
      )}
    </>
  )
}
